import { useCallback, useMemo, useState } from "react";
import { tailwind } from "@/utilities/tailwind-utils";
import { cn } from "@/utilities/app-utils";
import {
  isValidUserEmail,
  getAvatarFallback
} from "@/utilities/validation-utils";
import {
  getProfileRoute,
  useNotificationsRoute
} from "@/hooks/use-user-type-from-path";
import {
  UserIcon,
  NotificationIcon,
  LogoutIcon,
  DotsHorizontalIcon
} from "@/utilities/icon-utils";
import { useNavigate } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { LogoutDialog } from "@/components/security";
import { useUserContext } from "@/context/user-context";
import { Button } from "@/components/ui/button";

interface NavUserProps {
  variant?: "sidebar" | "header";
  className?: string;
}

export function NavUser({ variant = "sidebar", className }: NavUserProps) {
  const { user } = useUserContext();
  const navigate = useNavigate();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  // Profile is marketplace-only (practice / professional). Staff consoles have none.
  const profileRoute = getProfileRoute(user?.userType);
  const notificationsRoute = useNotificationsRoute();

  const handleLogoutClick = useCallback(() => {
    setShowLogoutDialog(true);
  }, []);

  const handleProfileClick = useCallback(() => {
    if (!profileRoute) return;
    navigate(profileRoute);
  }, [navigate, profileRoute]);

  const handleNotificationsClick = useCallback(() => {
    navigate(notificationsRoute);
  }, [navigate, notificationsRoute]);

  // Get display text for user info
  const getDisplayText = useCallback(() => {
    if (isValidUserEmail(user?.email || "")) {
      return user?.email;
    } else {
      return user?.userType;
    }
  }, [user?.email, user?.userType]);

  const dropDownMenuContents = useMemo(() => {
    const userName = `${user?.firstName} ${user?.lastName}`;
    const displayText = getDisplayText();
    return (
      <>
        <DropdownMenuLabel className={tailwind.components.navDropdownLabel}>
          <div
            className={tailwind.layout.flexCenterGap2 + " text-left text-sm"}
          >
            <Avatar
              className={
                tailwind.sizes.size8 + " " + tailwind.borders.roundedLg
              }
            >
              <AvatarImage src="" alt={userName} />
              <AvatarFallback className={tailwind.borders.roundedLg}>
                {getAvatarFallback(userName)}
              </AvatarFallback>
            </Avatar>
            <div
              className={
                tailwind.layout.flexCol +
                " flex-1 text-left text-sm leading-tight"
              }
            >
              <span
                className={
                  tailwind.typography.fontMedium + " truncate max-w-[180px]"
                }
                title={userName}
              >
                {userName}
              </span>
              <span
                className={
                  tailwind.typography.textXsMuted + " truncate max-w-[180px]"
                }
                title={displayText}
              >
                {displayText}
              </span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {profileRoute ? (
            <DropdownMenuItem onClick={handleProfileClick}>
              <UserIcon />
              Profile
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onClick={handleNotificationsClick}>
            <NotificationIcon />
            Notifications
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogoutClick}
          className="text-destructive focus:text-destructive"
        >
          <LogoutIcon />
          {"Sign out"}
        </DropdownMenuItem>
      </>
    );
  }, [
    user?.firstName,
    user?.lastName,
    getDisplayText,
    profileRoute,
    handleProfileClick,
    handleNotificationsClick,
    handleLogoutClick
  ]);

  // Render the user menu if user is authenticated
  if (!user) return null;

  // Render the nav user

  return (
    <>
      {/* Nav User Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {variant === "header" ? (
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-9 w-9 rounded-full", className)}
            >
              <Avatar className="h-8 w-8">
                <AvatarImage
                  src=""
                  alt={`${user.firstName} ${user.lastName}`}
                />
                <AvatarFallback className="text-xs">
                  {getAvatarFallback(`${user.firstName} ${user.lastName}`)}
                </AvatarFallback>
              </Avatar>
            </Button>
          ) : (
            <Button
              variant="ghost"
              className={cn("w-full justify-start gap-3 p-2 h-auto", className)}
            >
              <Avatar
                className={
                  tailwind.sizes.size8 +
                  " " +
                  tailwind.borders.roundedLg +
                  " grayscale"
                }
              >
                <AvatarImage
                  src=""
                  alt={`${user.firstName} ${user.lastName}`}
                />
                <AvatarFallback className={tailwind.borders.roundedLg}>
                  {getAvatarFallback(`${user.firstName} ${user.lastName}`)}
                </AvatarFallback>
              </Avatar>
              <div
                className={
                  tailwind.layout.flexCol +
                  " flex-1 text-left text-sm leading-tight"
                }
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1">
                        <span
                          className={
                            tailwind.typography.fontMedium +
                            " truncate cursor-help max-w-[140px]"
                          }
                        >
                          {`${user.firstName} ${user.lastName}`}
                        </span>
                        <DotsHorizontalIcon
                          className={
                            tailwind.sizes.size3 + " text-muted-foreground"
                          }
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" align="start">
                      {`${user.firstName} ${user.lastName}`}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <span className={tailwind.typography.textXsMuted + " truncate"}>
                  {getDisplayText()}
                </span>
              </div>
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className={tailwind.components.navDropdown}
          side={variant === "header" ? "bottom" : "right"}
          align={variant === "header" ? "start" : "end"}
          sideOffset={4}
        >
          {dropDownMenuContents}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Logout confirmation dialog */}
      {showLogoutDialog && (
        <LogoutDialog
          open={showLogoutDialog}
          onOpenChange={setShowLogoutDialog}
        />
      )}
    </>
  );
}
